import type { Module } from "vuex";
import { apiPaths } from "@/dictionaries/api/paths";
import type { Meet } from "@/types/meet";
import http from "@/utils/axios";
export interface Meeting {
  ExternalMeetingId: string;
  MediaPlacement: {
    AudioFallbackUrl: string;
    AudioHostUrl: string;
    EventIngestionUrl: string;
    ScreenDataUrl: string;
    ScreenSharingUrl: string;
    ScreenViewingUrl: string;
    SignalingUrl: string;
    TurnControlUrl: string;
  };
  MediaRegion: string;
  MeetingArn: string;
  MeetingFeatures: {
    Audio: {
      EchoReduction: string;
    };
  };
  MeetingHostId: string;
  MeetingId: string;
  PrimaryMeetingId: string;
  TenantIds: string[];
}
export interface Attendee {
  AttendeeId: string;
  Capabilities: {
    Audio: string;
    Content: string;
    Video: string;
  };
  ExternalUserId: string;
  JoinToken: string;
}

export interface CallState {
  isLoading: boolean;
  meetData: Meet | null;
  meeting: Meeting | null;
  attendee: Attendee | null;
  chatActive: boolean;
  chatInitialized: boolean;
  userMeetingStarted: boolean;
  isVideoInWindow: boolean;
  callIsMounted: boolean;
  phoneMounted: boolean;
  renderChatAndVideo: boolean;
  calendarUUID: string | null;
  conversation_token: string | null;
  conversation_id: string | null;
  messages: object[];
}

export interface DiagnosisItem {
  id: number;
  code: string;
}

interface MeetingResponse {
  Meeting: Meeting;
  Attendees: Attendee[];
}

const module: Module<CallState, unknown> = {
  namespaced: true,
  state: {
    meetData: null,
    meeting: null,
    attendee: null,
    isLoading: false,
    chatActive: false,
    chatInitialized: false,
    userMeetingStarted: false,
    isVideoInWindow: false,
    callIsMounted: false,
    phoneMounted: false,
    renderChatAndVideo: true,
    calendarUUID: null,
    conversation_token: null,
    conversation_id: null,
    messages: [],
  },
  getters: {
    isLoading: (state) => state.isLoading,
    isChatActive: (state) => state.chatActive,
    isChatInitialized: (state) => state.chatInitialized,
    getMeetingInfo: ({ attendee, meeting }) => ({ attendee, meeting }),
    getMeet: (state) => state.meetData,
    getMeeting: (state) => state.meeting,
    getUserMeetingStarted: (state) => state.userMeetingStarted,
    getIsVideoInWindow: (state) => state.isVideoInWindow,
    getCallIsMounted: (state) => state.callIsMounted,
    getPhoneMounted: (state) => state.phoneMounted,
    getRenderChatAndVideo: (state) => state.renderChatAndVideo,
    getCalendarUUID: (state) => state.calendarUUID,
    getConversationToken: (state) => state.conversation_token,
    getConversationId: (state) => state.conversation_id,
    getConversationMessages: (state) => state.messages,
    getMeetDiagnosis: (state) => state.meetData?.meet.diagnosis,
    getMeetInterview: (state) => state.meetData?.meet.case_history,
    getMeetRecommendations: (state) => state.meetData?.meet.recommendations,
  },
  mutations: {
    setLoading(state, payload: boolean) {
      state.isLoading = payload;
    },
    toggleChat(state) {
      state.chatActive = !state.chatActive;
    },
    setChatActive(state, payload: boolean) {
      state.chatActive = payload;
    },
    setChatInitialized(state, payload: boolean) {
      state.chatInitialized = payload;
    },
    setMeetingInfo(state, payload: MeetingResponse) {
      state.meeting = payload.Meeting;
      state.attendee = payload.Attendees[0];
    },
    setMeet(state, payload: Meet) {
      state.meetData = payload;
    },
    setUserMeetingStarted(state, value: boolean) {
      state.userMeetingStarted = value;
    },
    setIsVideoInWindow(state, value: boolean) {
      state.isVideoInWindow = value;
    },
    setCallIsMounted(state, value: boolean) {
      state.callIsMounted = value;
    },
    setPhoneMounted(state, value: boolean) {
      state.phoneMounted = value;
    },
    setRenderChatAndVideo(state, value: boolean) {
      state.renderChatAndVideo = value;
    },
    setCalendarUUID(state, value: string) {
      state.calendarUUID = value;
    },
    setMeetValue(state, data) {
      if (state.meetData?.meet) {
        Object.assign(state.meetData.meet, data);
      }
    },
    setMeetCaseHistory(state, payload: string) {
      if (state.meetData) {
        state.meetData.meet.case_history = payload;
      }
    },
    setMeetDiagnosis(state, payload: DiagnosisItem[]) {
      if (state.meetData) {
        state.meetData.meet.diagnosis = payload;
      }
    },
    setMeetRecommendations(state, payload: string) {
      if (state.meetData) {
        state.meetData.meet.recommendations = payload;
      }
    },
    setConversation(
      state,
      { conversation_id, token }: { conversation_id: string; token: string },
    ) {
      state.conversation_id = conversation_id;
      state.conversation_token = token;
    },
    setConversationMessages(state, payload: object[]) {
      state.messages = payload;
    },
    pushConversationMessage(state, payload: object) {
      state.messages.push(payload);
    },
    clearAllMeetingData(state) {
      state.meetData = null;
      state.attendee = null;
      state.isLoading = false;
      state.chatActive = false;
      state.chatInitialized = false;
      state.isVideoInWindow = false;
      state.callIsMounted = false;
      state.phoneMounted = false;
      state.userMeetingStarted = false;
      state.conversation_token = null;
      state.conversation_id = null;
    },
  },
  actions: {
    async fetchMeeting({ commit }, id) {
      commit("setLoading", true);
      const { data } = await http.get<MeetingResponse>(
        `${apiPaths.meet.activeOwner}/${id}`,
      );
      commit("setMeetingInfo", data);
      commit("setLoading", false);
    },
    setChatInitialized({ commit }, state) {
      commit("setChatInitialized", state);
    },
    async createCall(_, owner_id) {
      return http.post(apiPaths.meet.create, {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        owner_id,
        type: "video",
      });
    },
    async fetchMeet({ commit }, uid) {
      const { data } = await http.get<Meet>(`/meet/${uid}`);
      commit("setMeet", data);

      if (data.conversation_id) {
        commit("setConversation", data);
      }
    },
    updateMeetingCard({ state }) {
      return http.post(`/doctor/meetings/${state.meetData?.meet.uid}/card`, {
        recommendations: state.meetData?.meet.recommendations,
        case_history: state.meetData?.meet.case_history,
        diagnosis: state.meetData?.meet.diagnosis.map((item) => item.id),
      });
    },
    async fetchConversationMessages({ commit }, id) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { data } = await http.get(`/meet/${id}/messages`);
      commit("setConversationMessages", data.messages);
    },
  },
};

export default module;
